import { useEffect, useState } from "react";
import { sendVerificationEmail } from "../lib/auth-client.ts";
import { AUTH_COPY, authResultError, normalizeAuthError } from "../lib/auth-messages.ts";

export const RESEND_VERIFICATION_COOLDOWN_SECONDS = 60;

interface ResendVerificationButtonProps {
  email: string;
  callbackURL: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function resendVerificationButtonText(resending: boolean, cooldown: number): string {
  if (resending) return "Sending…";
  if (cooldown > 0) return `Resend in ${cooldown}s`;
  return "Need a new verification email? Resend it";
}

export function ResendVerificationButton({
  email,
  callbackURL,
  onSuccess,
  onError,
}: ResendVerificationButtonProps) {
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function handleResendVerification() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      onError(AUTH_COPY.missingEmailForVerification);
      return;
    }

    let handledByCallback = false;
    setResending(true);
    try {
      const res = await sendVerificationEmail(
        { email: trimmedEmail, callbackURL },
        {
          onRequest: () => setResending(true),
          onSuccess: () => {
            handledByCallback = true;
            setCooldown(RESEND_VERIFICATION_COOLDOWN_SECONDS);
            onSuccess(AUTH_COPY.verificationEmailSent);
          },
          onError: (ctx: { error: unknown }) => {
            handledByCallback = true;
            onError(normalizeAuthError(ctx.error, "Could not send a new verification email."));
          },
        },
      );

      if (!handledByCallback) {
        const message = authResultError(res, "Could not send a new verification email.");
        if (message) {
          onError(message);
          return;
        }
        setCooldown(RESEND_VERIFICATION_COOLDOWN_SECONDS);
        onSuccess(AUTH_COPY.verificationEmailSent);
      }
    } catch (err) {
      onError(normalizeAuthError(err, "Could not send a new verification email."));
    } finally {
      setResending(false);
    }
  }

  return (
    <button
      type="button"
      className="auth-inline-action"
      onClick={() => void handleResendVerification()}
      disabled={resending || cooldown > 0}
    >
      {resendVerificationButtonText(resending, cooldown)}
    </button>
  );
}
